import React, { useCallback, useMemo } from 'react';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  closeSnackbar,
  OptionsObject,
  SnackbarKey,
  SnackbarMessage,
  SnackbarProvider,
  useSnackbar as useNotistackSnackbar,
} from 'notistack';

/** Dismiss button shown on every toast, so persistent ones can be closed. */
const dismissAction = (key: SnackbarKey) => (
  <IconButton size="small" color="inherit" aria-label="Dismiss" onClick={() => closeSnackbar(key)}>
    <CloseIcon fontSize="small" />
  </IconButton>
);

/**
 * The app's toast host: at most three at once, bottom-left, each with a
 * dismiss button. Without it mounted, notistack's default context swallows
 * every enqueueSnackbar call.
 */
export const AppSnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SnackbarProvider
    maxSnack={3}
    autoHideDuration={5000}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    action={dismissAction}
  >
    {children}
  </SnackbarProvider>
);

type EnqueueSnackbar = (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;

/**
 * notistack's useSnackbar with one house rule: error toasts stay until the
 * user dismisses them, so a failed save can't disappear while they look away.
 */
export const useSnackbar = () => {
  const { enqueueSnackbar: enqueue, closeSnackbar: close } = useNotistackSnackbar();

  const enqueueSnackbar = useCallback<EnqueueSnackbar>(
    (message, options) =>
      enqueue(message, options?.variant === 'error' ? { persist: true, ...options } : options),
    [enqueue]
  );

  return useMemo(() => ({ enqueueSnackbar, closeSnackbar: close }), [enqueueSnackbar, close]);
};
